import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Settings, Upload, Loader2, ImageIcon, X, Save, Wrench, Pencil, Trash2, Plus, DatabaseBackup, Download, History, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import GestioneUtenti from "@/components/GestioneUtenti";
import GestioneLinkBolle from "@/components/GestioneLinkBolle";

export default function ImpostazioniPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasIcon, setHasIcon] = useState(false);
  const [iconVer, setIconVer] = useState(0);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [attrezzoEditId, setAttrezzoEditId] = useState(null);
  const [attrezzoForm, setAttrezzoForm] = useState({ nome: "", marca: "", modello: "", targa: "", azienda: "", costo_orario: "" });
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [sezione, setSezione] = useState("generali");

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({
      company_name: "", indirizzo: "", piva: "", telefono: "", email: "", logo_path: null, logo_nome: null,
    }));
    api.get("/branding/state").then((r) => setHasIcon(r.data.has_icon)).catch(() => {});
  }, []);

  const exportBackup = async () => {
    setBackupBusy(true);
    try {
      const response = await api.post("/gestionale/backup/export", null, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CantierePro_Backup_${new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-")}.cprobackup`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Backup creato e scaricato");
      const status = await api.get("/gestionale/backup/status"); setBackupStatus(status.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore creazione backup"); }
    finally { setBackupBusy(false); }
  };

  const importBackup = async (file) => {
    if (!file) return;
    if (!window.confirm("Il ripristino sostituirà i dati attuali. Prima verrà creato automaticamente un backup di sicurezza. Continuare?")) return;
    setBackupBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/gestionale/backup/import", fd, { params: { scope: "full" }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Backup del ${new Date(r.data.backup_date).toLocaleString("it-IT")} ripristinato`);
      const status = await api.get("/gestionale/backup/status"); setBackupStatus(status.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Ripristino non riuscito"); }
    finally { setBackupBusy(false); }
  };

  const resetAttrezzo = () => {
    setAttrezzoEditId(null);
    setAttrezzoForm({ nome: "", marca: "", modello: "", targa: "", azienda: "", costo_orario: "" });
  };

  const salvaAttrezzo = async (e) => {
    e.preventDefault();
    if (!attrezzoForm.nome.trim()) { toast.error("Inserisci il nome dell'attrezzo"); return; }
    const payload = { ...attrezzoForm, costo_orario: Number(String(attrezzoForm.costo_orario).replace(",", ".")) || 0 };
    try {
      if (attrezzoEditId) await api.put(`/attrezzi-catalogo/${attrezzoEditId}`, payload);
      else await api.post("/attrezzi-catalogo", payload);
      const r = await api.get("/attrezzi-catalogo");
      setCatalogo(Array.isArray(r.data) ? r.data : []);
      toast.success(attrezzoEditId ? "Attrezzo aggiornato" : "Attrezzo aggiunto");
      resetAttrezzo();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore salvataggio attrezzo"); }
  };

  const modificaAttrezzo = (item) => {
    setAttrezzoEditId(item.id);
    setAttrezzoForm({ nome: item.nome || "", marca: item.marca || "", modello: item.modello || "", targa: item.targa || "", azienda: item.azienda || "", costo_orario: String(item.costo_orario ?? "") });
  };

  const eliminaAttrezzo = async (item) => {
    if (!window.confirm(`Eliminare l'attrezzo "${item.nome}" dal catalogo?`)) return;
    try {
      await api.delete(`/attrezzi-catalogo/${item.id}`);
      setCatalogo((v) => v.filter((x) => x.id !== item.id));
      toast.success("Attrezzo eliminato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore eliminazione"); }
  };

  const uploadIcon = async (file) => {
    if (!file) return;
    setUploadingIcon(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/branding/icon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasIcon(true); setIconVer((v) => v + 1);
      toast.success("Icona app aggiornata. Reinstalla l'app per vederla in home.");
    } catch (e) { toast.error("Errore caricamento icona"); }
    finally { setUploadingIcon(false); }
  };
  const removeIcon = async () => {
    try { await api.delete("/branding/icon"); setHasIcon(false); setIconVer((v) => v + 1); toast.success("Icona ripristinata"); }
    catch (e) { toast.error("Errore"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/bolla", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: data.path, logo_nome: data.nome }));
      toast.success("Logo caricato");
    } catch (e) { toast.error("Errore caricamento logo"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell>
      <button data-testid="back-button" onClick={() => navigate("/")}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>

      <div className="mb-6">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex items-center gap-2"><Settings className="h-4 w-4" /> Impostazioni</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold mt-2">Impostazioni</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b pb-4">
        <Button type="button" variant={sezione === "generali" ? "default" : "outline"} onClick={() => setSezione("generali")}>
          <Settings className="h-4 w-4 mr-2" /> Impostazioni generali
        </Button>
        <Button type="button" variant={sezione === "backup" ? "default" : "outline"} onClick={async () => {
          setSezione("backup");
          try { const r = await api.get("/gestionale/backup/status"); setBackupStatus(r.data); } catch (_) {}
        }}>
          <DatabaseBackup className="h-4 w-4 mr-2" /> Backup e ripristino
        </Button>
        <Button type="button" variant={sezione === "bolle" ? "default" : "outline"} onClick={() => setSezione("bolle")}>
          <Link2 className="h-4 w-4 mr-2" /> Link bolle operai
        </Button>
      </div>

      {sezione === "generali" ? <>
      <p className="text-muted-foreground mb-6 max-w-2xl">Carta intestata, logo, icona dell'app e gestione utenti.</p>

      {/* App icon */}
      <div className="max-w-3xl rounded-xl border border-border bg-card p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <img src={`${API}/branding/icon-192.png?v=${iconVer}`} alt="Icona app" data-testid="app-icon-preview"
            className="h-16 w-16 rounded-xl border border-border object-cover shrink-0" />
          <div className="flex-1">
            <p className="font-heading font-semibold">Icona dell'app</p>
            <p className="text-sm text-muted-foreground">L'immagine mostrata quando installi l'app sul telefono. Consigliata quadrata.</p>
          </div>
          <div className="flex gap-2">
            <label data-testid="app-icon-upload" className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border font-bold px-4 h-11 hover:border-primary hover:text-primary transition-colors">
              {uploadingIcon ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadIcon(e.target.files?.[0])} />
            </label>
            {hasIcon && (
              <Button type="button" variant="ghost" data-testid="app-icon-remove" onClick={removeIcon} className="h-11 hover:text-destructive">
                <X className="h-4 w-4 mr-1" /> Ripristina
              </Button>
            )}
          </div>
        </div>
      </div>

      {form === null ? (
        <Skeleton className="h-96 rounded-xl max-w-3xl" />
      ) : (
        <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={save} className="max-w-3xl grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Logo */}
          <div className="lg:col-span-1">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Logo aziendale</Label>
            <div className="mt-2 rounded-xl border border-border bg-card p-4">
              <div className="aspect-video rounded-lg bg-muted/50 border border-dashed border-border flex items-center justify-center overflow-hidden mb-3">
                {form.logo_path ? (
                  <img src={`${API}/uploads/bolla/${form.logo_path}`} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                ) : (
                  <ImageIcon className="h-10 w-10 text-muted-foreground/40" />
                )}
              </div>
              {form.logo_path ? (
                <Button type="button" variant="outline" size="sm" data-testid="logo-remove" onClick={() => setForm({ ...form, logo_path: null, logo_nome: null })} className="w-full rounded-md">
                  <X className="h-4 w-4 mr-1" /> Rimuovi logo
                </Button>
              ) : (
                <label data-testid="logo-upload" className="flex items-center justify-center gap-2 cursor-pointer rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Caricamento..." : "Carica logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className="lg:col-span-2 space-y-4 rounded-xl border border-border bg-card p-6">
            <div className="space-y-2">
              <Label>Ragione sociale</Label>
              <Input data-testid="company-name-input" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Edil Costruzioni SRL" />
            </div>
            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input data-testid="company-indirizzo-input" value={form.indirizzo || ""} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} placeholder="Via Roma 12, 20100 Milano" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>P.IVA</Label>
                <Input data-testid="company-piva-input" value={form.piva || ""} onChange={(e) => setForm({ ...form, piva: e.target.value })} placeholder="IT01234567890" />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input data-testid="company-telefono-input" value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39 02 1234567" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="company-email-input" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@azienda.it" />
            </div>
            <Button type="submit" disabled={saving} data-testid="save-settings-button" className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva impostazioni
            </Button>
          </div>
        </motion.form>
      )}
      <GestioneUtenti />
      </> : sezione === "bolle" ? <GestioneLinkBolle /> : <div className="max-w-3xl space-y-6">
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><DatabaseBackup className="h-6 w-6" /></div>
            <div><h2 className="font-heading text-2xl font-bold">Backup completo</h2><p className="text-muted-foreground mt-1">Scarica dati, documenti e configurazione in un unico file protetto.</p></div>
          </div>
          <Button type="button" onClick={exportBackup} disabled={backupBusy} className="mt-6">
            {backupBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Crea e scarica backup
          </Button>
        </div>
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0"><History className="h-6 w-6" /></div>
            <div><h2 className="font-heading text-2xl font-bold">Ripristina backup</h2><p className="text-muted-foreground mt-1">Prima del ripristino viene creato automaticamente un backup di sicurezza dei dati attuali.</p></div>
          </div>
          <label className="mt-6 inline-flex h-10 items-center gap-2 rounded-md border px-4 font-semibold cursor-pointer hover:border-primary hover:text-primary">
            <Upload className="h-4 w-4" /> Seleziona file .cprobackup
            <input type="file" accept=".cprobackup" className="hidden" disabled={backupBusy} onChange={(e) => { importBackup(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </div>
        <div className="rounded-xl border bg-muted/30 p-5 text-sm">
          <p className="font-semibold">Stato backup</p>
          <p className="text-muted-foreground mt-1">{backupStatus?.last_backup_at ? `Ultimo backup: ${new Date(backupStatus.last_backup_at).toLocaleString("it-IT")}` : "Nessun backup registrato in questa installazione."}</p>
        </div>
      </div>}
    </AppShell>
  );
}

